import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n";
import Layout from "@/components/Layout";
import {
  Home, HowItWorks, Science, AnalysisPage, KitPage, HairScore, Pricing, ForProfessionals,
  ForLaboratories, LabPartners, About, Faq, Legal,
} from "@/pages/Public";
import { Login, Register, ForgotPassword, ResetPassword, Contact, AuthCallback, ConfirmEmail } from "@/pages/Auth";
import { FreeDiagnostic, Questionnaire, Order, PaymentResult } from "@/pages/Order";
import Account from "@/pages/Account";
import LabPortal from "@/pages/LabPortal";
import Admin from "@/pages/Admin";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const Protected = ({ roles, children }) => {
  const { user, loading, homeFor } = useAuth();
  if (loading) return <div className="px-6 py-32 text-sm text-zinc-500">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
};

const Public = ({ children }) => <Layout>{children}</Layout>;

const PUBLIC_ROUTES = [
  ["/", <Home />],
  ["/how-it-works", <HowItWorks />],
  ["/science", <Science />],
  ["/analysis", <AnalysisPage />],
  ["/kit", <KitPage />],
  ["/hair-health-score", <HairScore />],
  ["/pricing", <Pricing />],
  ["/for-professionals", <ForProfessionals />],
  ["/for-laboratories", <ForLaboratories />],
  ["/laboratory-partners", <LabPartners />],
  ["/about", <About />],
  ["/faq", <Faq />],
  ["/contact", <Contact />],
  ["/legal/privacy", <Legal />],
  ["/legal/terms", <Legal />],
  ["/legal/cookies", <Legal />],
  ["/login", <Login />],
  ["/register", <Register />],
  ["/forgot-password", <ForgotPassword />],
  ["/reset-password", <ResetPassword />],
  ["/auth/callback", <AuthCallback />],
  ["/auth/confirm", <ConfirmEmail />],
  ["/diagnostic-gratuit", <FreeDiagnostic />],
];

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <ScrollToTop />
          <Routes>
            {PUBLIC_ROUTES.map(([path, element]) => (
              <Route key={path} path={path} element={<Public>{element}</Public>} />
            ))}
            <Route path="/order" element={<Public><Protected roles={["client", "admin"]}><Order /></Protected></Public>} />
            <Route path="/questionnaire" element={<Public><Protected roles={["client", "admin"]}><Questionnaire /></Protected></Public>} />
            <Route path="/payment/success" element={<Public><PaymentResult ok /></Public>} />
            <Route path="/payment/cancel" element={<Public><PaymentResult ok={false} /></Public>} />
            <Route path="/account" element={<Protected roles={["client"]}><Account /></Protected>} />
            <Route path="/lab" element={<Protected roles={["lab_technician", "lab_reviewer", "admin"]}><LabPortal /></Protected>} />
            <Route path="/lab/scan/:sampleId" element={<Protected roles={["lab_technician", "lab_reviewer", "admin"]}><LabPortal /></Protected>} />
            <Route path="/admin" element={<Protected roles={["admin"]}><Admin /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" />
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
